import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, Plus, Check, Loader2,
  ShoppingCart, Scan, Info, Eye, EyeOff, Heart,
} from 'lucide-react';
import { Button } from '../ui/button';
import SportAIOverlay from './SportAIOverlay';
import SportPhotoInfo from './SportPhotoInfo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportLightbox({
  photos,
  currentIndex,
  onIndexChange,
  onClose,
  cartPhotoIds = new Set(),
  addingToCart = {},
  favoriteIds = new Set(),
  onToggleFavorite,
  pricing = null,
  onAddToCart,
  language = 'pl',
  t = {},
}) {
  const [showAI, setShowAI] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [photoDetail, setPhotoDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const imgRef = useRef(null);
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });

  const photo = photos[currentIndex];
  const photoId = photo?.id;

  // Fetch full photo detail (with AI tags enriched with names)
  useEffect(() => {
    if (!photoId) return;
    let cancelled = false;
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const res = await axios.get(`${API}/sport/public/photos/${photoId}`);
        if (!cancelled) setPhotoDetail(res.data);
      } catch (err) {
        console.error('Failed to load photo detail:', err);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [photoId]);

  // Update image display dimensions
  const updateImgDims = useCallback(() => {
    if (imgRef.current) {
      setImgDims({
        width: imgRef.current.clientWidth,
        height: imgRef.current.clientHeight,
      });
    }
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(updateImgDims);
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [updateImgDims, photoId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') onIndexChange?.((currentIndex - 1 + photos.length) % photos.length);
      else if (e.key === 'ArrowRight') onIndexChange?.((currentIndex + 1) % photos.length);
      else if (e.key === 'i' || e.key === 'I') setShowInfo(v => !v);
      else if (e.key === 'a' || e.key === 'A') setShowAI(v => !v);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, photos.length, onClose, onIndexChange]);

  if (!photo) return null;

  const inCart = cartPhotoIds.has(photo.id);
  const isAdding = addingToCart[photo.id];
  const isFav = favoriteIds.has(photo.id);
  const detailPhoto = photoDetail?.id === photo.id ? photoDetail : photo;
  const aiTags = detailPhoto.ai_tags || [];
  const imageW = detailPhoto.variants?.width || 0;
  const imageH = detailPhoto.variants?.height || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      >
        {/* Close */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </Button>

        {/* Prev */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
            onClick={() => onIndexChange?.((currentIndex - 1 + photos.length) % photos.length)}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
        )}

        {/* Next */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
            onClick={() => onIndexChange?.((currentIndex + 1) % photos.length)}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        )}

        {/* Image container */}
        <div className={`relative flex items-center justify-center transition-all ${showInfo ? 'mr-72' : ''}`}>
          <motion.img
            key={photo.id}
            ref={imgRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            src={photo.variants?.preview || photo.variants?.full || photo.variants?.thumbnail}
            alt={photo.filename}
            className="max-h-[85vh] max-w-[85vw] object-contain"
            onLoad={updateImgDims}
          />

          {/* AI Overlay */}
          {showAI && aiTags.length > 0 && imgDims.width > 0 && (
            <SportAIOverlay
              aiTags={aiTags}
              imageWidth={imageW}
              imageHeight={imageH}
              displayWidth={imgDims.width}
              displayHeight={imgDims.height}
              showLabels={showLabels}
            />
          )}
        </div>

        {/* Bottom bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Cart button */}
              <div onClick={e => e.stopPropagation()}>
                {inCart ? (
                  <Button size="sm" variant="secondary" className="text-xs" disabled>
                    <Check className="w-3 h-3 mr-1" /> {t.inCart || 'In cart'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="text-xs"
                    onClick={() => onAddToCart?.(photo.id)}
                    disabled={isAdding}
                  >
                    {isAdding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShoppingCart className="w-3 h-3 mr-1" />}
                    {pricing ? `${pricing.per_photo_price} ${pricing.currency}` : (t.addToCart || 'Add')}
                  </Button>
                )}
              </div>

              {/* Favorite toggle */}
              {onToggleFavorite && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-xs hover:bg-white/10 ${isFav ? 'text-red-500' : 'text-white'}`}
                  onClick={() => onToggleFavorite(photo.id)}
                >
                  <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
                </Button>
              )}

              {/* AI toggle */}
              {aiTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-xs text-white hover:bg-white/10 ${showAI ? 'bg-white/20' : ''}`}
                  onClick={() => setShowAI(v => !v)}
                >
                  <Scan className="w-4 h-4 mr-1" />
                  AI ({aiTags.length})
                </Button>
              )}

              {/* Labels toggle (when AI is on) */}
              {showAI && aiTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-white hover:bg-white/10"
                  onClick={() => setShowLabels(v => !v)}
                >
                  {showLabels ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
              )}

              {/* Info toggle */}
              <Button
                variant="ghost"
                size="sm"
                className={`text-xs text-white hover:bg-white/10 ${showInfo ? 'bg-white/20' : ''}`}
                onClick={() => setShowInfo(v => !v)}
              >
                <Info className="w-4 h-4 mr-1" />
                Info
              </Button>
            </div>

            {/* Filename */}
            <span className="text-white/40 text-xs font-mono hidden sm:block">
              {photo.filename}
            </span>
          </div>

          {/* Counter */}
          <div className="text-center mt-2 text-white/50 text-sm">
            {currentIndex + 1} / {photos.length}
          </div>
        </div>

        {/* Info Panel */}
        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial={{ x: 288, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 288, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-72 bg-black/90 border-l border-white/10 p-5 overflow-y-auto z-40"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium text-sm">
                  {language === 'pl' ? 'Szczegoly' : 'Details'}
                </h3>
                <button onClick={() => setShowInfo(false)} className="text-white/40 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {detailLoading && photoDetail?.id !== photo.id ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-white/40" />
                </div>
              ) : (
                <SportPhotoInfo photo={detailPhoto} language={language} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
